import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { useDebounce } from '../../hooks/useDebounce';
import EmployeeCard from './EmployeeCard';
import DepartmentFilterSidebar from './DepartmentFilterSidebar';
import './directory.css';

// The directory loads in ONE request rather than paging: the roster is a few
// hundred people, everybody is looking for a specific person, and a "load more"
// button hid two thirds of them behind a click that a search cannot see past.
// The ceiling matches the server's own cap on `limit`.
const FETCH_LIMIT = 2000;

/** Read a "?dept=5,9" param into a numeric id list, dropping junk segments. */
function parseDeptParam(raw) {
  if (!raw) return [];
  const out = [];
  for (const part of String(raw).split(',')) {
    const n = Number.parseInt(part, 10);
    if (Number.isInteger(n) && n > 0 && !out.includes(n)) out.push(n);
  }
  return out;
}

export default function EmployeeDirectoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [term, setTerm] = useState(searchParams.get('q') || '');
  // Multi-select: the URL carries a csv list ("?dept=5,9") so a filtered view
  // stays shareable and survives a reload.
  const [deptIds, setDeptIds] = useState(() => parseDeptParam(searchParams.get('dept')));
  const debouncedTerm = useDebounce(term, 300);
  // Joined for the effect dependency: comparing the array identity would refetch
  // on every render, since a new array is produced each time.
  const deptKey = deptIds.join(',');

  const [departments, setDepartments] = useState([]);
  const [deptsLoading, setDeptsLoading] = useState(true);
  // A failed department read must not read as "this org has no departments".
  const [deptsError, setDeptsError] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const reqIdRef = useRef(0);

  // Department rail options: once on mount, and again from the rail's retry.
  const deptsActiveRef = useRef(true);
  const loadDepartments = useCallback(() => {
    setDeptsLoading(true);
    setDeptsError(false);
    axios
      .get(`${API_BASE_URL}/api/directory/departments`, { withCredentials: true })
      .then((r) => {
        if (!deptsActiveRef.current) return;
        setDepartments(r.data.departments || []);
        setDeptsLoading(false);
      })
      .catch(() => { if (deptsActiveRef.current) { setDeptsError(true); setDeptsLoading(false); } });
  }, []);
  useEffect(() => {
    deptsActiveRef.current = true;
    loadDepartments();
    return () => { deptsActiveRef.current = false; };
  }, [loadDepartments]);

  const fetchAll = useCallback(async () => {
    reqIdRef.current += 1;
    const myReqId = reqIdRef.current;
    setLoading(true);
    // Cleared up front, not on success: otherwise a retry after a failure
    // renders nothing at all (no error, no loading, no grid) until it resolves.
    setError(false);
    try {
      const params = { limit: FETCH_LIMIT, offset: 0 };
      if (debouncedTerm) params.q = debouncedTerm;
      if (deptKey) params.dept = deptKey;
      const r = await axios.get(`${API_BASE_URL}/api/directory`, { params, withCredentials: true });
      // A slower earlier request must not overwrite a newer one's results.
      if (myReqId !== reqIdRef.current) return;
      const data = r.data;
      setError(false);
      setTotal(data.total || 0);
      setEmployees(data.employees || []);
    } catch {
      // Whole-page load failure: a full error state below, not a toast.
      if (myReqId !== reqIdRef.current) return;
      setError(true);
      setEmployees([]);
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [debouncedTerm, deptKey]);

  // Refetch whenever the search term or dept selection changes.
  useEffect(() => {
    // sync URL state
    const next = {};
    if (debouncedTerm) next.q = debouncedTerm;
    if (deptKey) next.dept = deptKey;
    setSearchParams(next, { replace: true });
    fetchAll();
  }, [debouncedTerm, deptKey, fetchAll, setSearchParams]);

  const toggleDept = useCallback((id) => {
    setDeptIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);
  const clearDepts = useCallback(() => setDeptIds([]), []);
  const clearAll = useCallback(() => { setTerm(''); setDeptIds([]); }, []);
  const hasTerm = debouncedTerm.trim().length > 0;
  // "Loading…" replaces the grid only on the first load. A refetch while typing
  // keeps the current cards (dimmed) so the page doesn't collapse and re-expand
  // on every debounced keystroke.
  const firstLoad = loading && employees.length === 0;

  return (
    <div className="main-content-inner">
      <header className="page-head">
        <p className="page-eyebrow">People</p>
        <div className="page-title-row">
          <h1 className="page-title">Employee Directory</h1>
        </div>
        <p className="page-subtitle">Find anyone at Robusta by name, job title, or email, or filter by department.</p>
      </header>

      <a
        className="dir-skip btn btn-secondary btn-small"
        href="#dir-search"
        onClick={(e) => { e.preventDefault(); document.getElementById('dir-search')?.focus(); }}
      >
        Skip to people search
      </a>
      <div className="dir-layout">
        <DepartmentFilterSidebar
          departments={departments}
          selected={deptIds}
          onToggle={toggleDept}
          onClear={clearDepts}
          loading={deptsLoading}
          error={deptsError}
          onRetry={loadDepartments}
        />

        <div className="dir-main">
          <div className="dir-controls">
            <input
              id="dir-search"
              type="search"
              className="search-input"
              placeholder="Search by name, title, or email…"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              aria-label="Search employees"
            />
          </div>

          <p className="dir-resultcount" role="status" aria-live="polite">
            {/* Hidden with the empty state: "0 people" above "No one matches"
                says the same thing twice. */}
            {!loading && !error && employees.length > 0 && (
              <>
                {total} {total === 1 ? 'person' : 'people'}
                {deptIds.length > 0 && ` in ${deptIds.length} department${deptIds.length === 1 ? '' : 's'}`}
              </>
            )}
          </p>

          <div className="dir-results" aria-busy={loading}>
            {firstLoad && <div className="dir-empty">Loading…</div>}

            {!loading && error && (
              <div className="dir-error" role="alert">
                <p>Couldn&apos;t load the directory.</p>
                <button type="button" className="btn btn-secondary btn-small" onClick={fetchAll}>Try again</button>
              </div>
            )}

            {!loading && !error && employees.length === 0 && (
              <div className="dir-empty">
                <p>
                  {hasTerm
                    ? <>No one matches &ldquo;{debouncedTerm.trim()}&rdquo;{deptIds.length > 0 ? ' in the selected departments' : ''}.</>
                    : deptIds.length > 0
                      ? 'No one in the selected departments.'
                      : 'No one in the directory yet.'}
                </p>
                {(hasTerm || deptIds.length > 0) && (
                  <button type="button" className="btn btn-secondary btn-small" onClick={clearAll}>
                    {deptIds.length > 0 ? 'Clear filters' : 'Clear search'}
                  </button>
                )}
              </div>
            )}

            {employees.length > 0 && (
              <>
                {/* Gives the card h3s a parent heading: without it every person
                    nests under the department rail's h2 in a heading list. */}
                <h2 className="sr-only" id="dir-results-heading">People</h2>
                <ul
                  className={`dir-grid${loading ? ' is-refreshing' : ''}`}
                  role="list"
                  aria-labelledby="dir-results-heading"
                >
                  {employees.map((emp) => (
                    <li key={emp.key || emp.id}><EmployeeCard employee={emp} /></li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
